import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Breadcrumb,
  BreadcrumbItem,
  Alert,
  Badge,
  Card,
  CardBody,
  CardTitle,
  Modal,
  ModalHeader,
  ModalBody,
  Spinner,
} from 'reactstrap';

import Widget from '../../../components/Widget';
import config from '../../../config';

// ─── Document metadata ───────────────────────────────────────────────────────
const DOC_TYPES = [
  { key: 'dl',        label: 'Driving License',          icon: 'fa-id-card' },
  { key: 'rc',        label: 'Registration Certificate', icon: 'fa-file-text' },
  { key: 'insurance', label: 'Insurance',                icon: 'fa-shield' },
  { key: 'emission',  label: 'Emission / PUC',           icon: 'fa-leaf' },
  { key: 'vehicle',   label: 'Vehicle Photo',            icon: 'fa-truck' },
];

// ─── Small component: single document card ───────────────────────────────────
function DocCard({ docType, docData, vehicleId, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileRef = useRef(null);

  const present = docData?.present;
  const dataFields = docData?.data || {};
  const imageUrl = docData?.url;

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      const token = localStorage.getItem('token');
      const form = new FormData();
      form.append('document_type', docType.key);
      form.append('file', file);
      const res = await fetch(
        `${config.opsApiBase.replace('/ops', '')}/ops/onboarding/driver-documents/${vehicleId}/upload`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form }
      );
      const data = await res.json();
      if (!data.success) {
        setUploadError(data.message || 'Upload failed');
      } else {
        onRefresh();
      }
    } catch (err) {
      setUploadError(err.message || 'Upload error');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div style={{ border: '1px solid #dee2e6', borderRadius: 8, marginBottom: 12, overflow: 'hidden' }}>
      {/* Header row */}
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px', background: present ? '#f0fff4' : '#fff5f5',
          cursor: 'pointer', userSelect: 'none',
        }}
        onClick={() => setExpanded(v => !v)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <i className={`fa ${docType.icon}`} style={{ color: present ? '#28a745' : '#dc3545', fontSize: 18 }} />
          <span style={{ fontWeight: 600, fontSize: 14 }}>{docType.label}</span>
          <Badge color={present ? 'success' : 'danger'} pill>
            {present ? 'Present' : 'Missing'}
          </Badge>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <Button
            size="sm"
            color={present ? 'secondary' : 'warning'}
            onClick={(e) => { e.stopPropagation(); fileRef.current && fileRef.current.click(); }}
            disabled={uploading}
          >
            {uploading
              ? <><Spinner size="sm" className="mr-1" />Uploading...</>
              : present ? <><i className="fa fa-refresh mr-1" />Update</> : <><i className="fa fa-upload mr-1" />Upload</>
            }
          </Button>
          <i className={`fa fa-chevron-${expanded ? 'up' : 'down'}`} style={{ color: '#6c757d' }} />
        </div>
      </div>

      {/* Upload error */}
      {uploadError && (
        <Alert color="danger" className="mb-0" style={{ borderRadius: 0, padding: '6px 14px', fontSize: 13 }}>
          <i className="fa fa-exclamation-circle mr-1" />{uploadError}
        </Alert>
      )}

      {/* Expanded section */}
      {expanded && (
        <div style={{ padding: '12px 14px', background: '#fff' }}>
          {/* Document image */}
          {imageUrl && imageUrl !== 'manual-entry' && (
            <div style={{ marginBottom: 12 }}>
              <a href={imageUrl} target="_blank" rel="noopener noreferrer">
                <img
                  src={imageUrl}
                  alt={docType.label}
                  style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 6, border: '1px solid #dee2e6', objectFit: 'contain' }}
                />
              </a>
              <div style={{ fontSize: 11, color: '#6c757d', marginTop: 4 }}>
                <a href={imageUrl} target="_blank" rel="noopener noreferrer">Open full image</a>
              </div>
            </div>
          )}

          {/* Extracted data fields */}
          {Object.keys(dataFields).length > 0 ? (
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <tbody>
                {Object.entries(dataFields).map(([k, v]) => (
                  v !== null && v !== undefined && typeof v !== 'object' && (
                    <tr key={k} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '4px 8px', fontWeight: 600, color: '#495057', width: '40%', textTransform: 'capitalize' }}>
                        {k.replace(/_/g, ' ')}
                      </td>
                      <td style={{ padding: '4px 8px', color: '#212529' }}>{String(v)}</td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          ) : (
            <p style={{ color: '#6c757d', fontSize: 13, margin: 0 }}>
              {present ? 'No extracted data available.' : 'No document uploaded yet.'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

const VehicleEdit = () => {
  const history = useHistory();
  const { id } = useParams();
  
  const [formData, setFormData] = useState({
    // Vehicle fields
    registration_no: '',
    make: '',
    model: '',
    class_: '',
    base_place: '',
    base_lat: '',
    base_lng: '',
    status: '',
    is_active: true,
    is_blocked: false,
    is_part: false,
    insurance_number: '',
    insurance_expiry: '',
    capacity: '',
    body_type: '',
    specialization: '',
    // Driver fields (editable)
    driver_name: '',
    driver_phone: '',
    driver_language_preference: '',
    driver_active_km_rate: '',
    driver_dead_km_rate: '',
    driver_verification_status: '',
    // Driver fields (read-only)
    driver_license_number: '',
    driver_license_expiry: '',
    driver_rating: '',
    driver_total_trips: '',
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [vehicleId, setVehicleId] = useState(null);
  const [driverId, setDriverId] = useState(null);
  const [driverUserId, setDriverUserId] = useState(null);
  const [initialDriverPhone, setInitialDriverPhone] = useState('');
  const [isDriverPhoneEditable, setIsDriverPhoneEditable] = useState(false);
  /** Registered owner when different from assigned driver (from API `owner`) */
  const [ownerInfo, setOwnerInfo] = useState(null);

  // Vehicle Docs modal
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState('');
  const [driverDocs, setDriverDocs] = useState(null);  // { dl, rc, insurance, emission, vehicle }

  const fetchVehicleData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.dispatchApiBase}/vehicles/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Vehicle data received:', data); // Debug log
        setVehicleId(data.vehicle_id);
        setDriverId(data.driver?.driver_id || null);
        setDriverUserId(data.driver?.user_id || null);
        setOwnerInfo(data.owner && data.owner.user_id ? data.owner : null);
        setInitialDriverPhone(data.driver?.phone || '');
        setIsDriverPhoneEditable(false);
        
        // Ensure all fields are populated, including read-only ones
        setFormData({
          registration_no: data.registration_no || '',
          make: data.make || '',
          model: data.model || '',
          class_: String(data.class_ || ''),
          base_place: '',
          base_lat: data.base_lat !== null ? String(data.base_lat) : '',
          base_lng: data.base_lng !== null ? String(data.base_lng) : '',
          status: data.status || 'available',
          is_active: data.is_active !== undefined ? data.is_active : true,
          is_blocked: data.is_blocked !== undefined ? data.is_blocked : false,
          is_part: data.is_part !== undefined ? data.is_part : false,
          insurance_number: data.insurance_number || '',
          insurance_expiry: data.insurance_expiry || '',
          capacity: data.capacity !== null ? String(data.capacity) : '',
          body_type: data.body_type || '',
          specialization: data.specialization || '',
          // Driver fields (editable)
          driver_name: data.driver?.name || '',
          driver_phone: data.driver?.phone || '',
          driver_language_preference: data.driver?.language_preference || '',
          driver_active_km_rate: data.driver?.active_km_rate !== null ? String(data.driver.active_km_rate) : '',
          driver_dead_km_rate: data.driver?.dead_km_rate !== null ? String(data.driver.dead_km_rate) : '',
          driver_verification_status: data.driver?.verification_status || '',
          // Driver fields (read-only)
          driver_license_number: data.driver?.license_number || '',
          driver_license_expiry: data.driver?.license_expiry || '',
          driver_rating: data.driver?.rating !== null ? String(data.driver.rating) : '',
          driver_total_trips: data.driver?.total_trips_completed !== null ? String(data.driver.total_trips_completed) : '0',
        });
        setIsLoading(false);
      } else {
        setError('Failed to load vehicle details');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error fetching vehicle:', error);
      setError('Error loading vehicle details');
      setIsLoading(false);
    }
  };

  const escapeSqlString = (value) => String(value ?? '').replace(/'/g, "''");

  const normalizedPhone = (value) => String(value ?? '').trim();

  const shouldShowPhoneSqlPreview =
    isDriverPhoneEditable &&
    normalizedPhone(formData.driver_phone) !== normalizedPhone(initialDriverPhone) &&
    normalizedPhone(formData.driver_phone) !== '';

  const phoneUpdateSql = shouldShowPhoneSqlPreview
    ? [
        'BEGIN;',
        '',
        'UPDATE users.users',
        `SET phone_number = '${escapeSqlString(normalizedPhone(formData.driver_phone))}'`,
        `WHERE id = ${driverUserId ?? '/* missing user_id */'};`,
        '',
        'UPDATE users.drivers',
        `SET phone = '${escapeSqlString(normalizedPhone(formData.driver_phone))}'`,
        `WHERE id = ${driverId ?? '/* missing driver_id */'};`,
        '',
        'COMMIT;',
      ].join('\n')
    : '';

  const copyPhoneSql = async () => {
    if (!phoneUpdateSql) return;
    try {
      await navigator.clipboard.writeText(phoneUpdateSql);
      alert('SQL copied to clipboard.');
    } catch (e) {
      // Fallback for older browsers / permission issues
      const textarea = document.createElement('textarea');
      textarea.value = phoneUpdateSql;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      alert('SQL copied to clipboard.');
    }
  };

  const fetchDriverDocs = async (vid) => {
    setDocsLoading(true);
    setDocsError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${config.opsApiBase.replace('/ops', '')}/ops/onboarding/driver-documents/${vid}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.success) {
        setDriverDocs(data.documents);
      } else {
        setDocsError(data.message || 'Failed to load documents');
      }
    } catch (err) {
      setDocsError(err.message || 'Error loading documents');
    } finally {
      setDocsLoading(false);
    }
  };

  const openDocsModal = () => {
    setShowDocsModal(true);
    if (vehicleId) fetchDriverDocs(vehicleId);
  };

  useEffect(() => {
    fetchVehicleData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleToggleActiveState = async () => {
    const newActiveState = !formData.is_active;
    const confirmMessage = newActiveState 
      ? 'Are you sure you want to ACTIVATE this vehicle?'
      : 'Are you sure you want to DEACTIVATE this vehicle?';
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.dispatchApiBase}/vehicles/${id}/active?active=${newActiveState}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        setFormData(prev => ({ ...prev, is_active: newActiveState }));
        alert(`Vehicle ${newActiveState ? 'activated' : 'deactivated'} successfully!`);
      } else {
        const data = await response.json();
        alert(`Failed to update state: ${data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error toggling vehicle state:', error);
      alert('Error updating vehicle state');
    }
  };

  const handleToggleBlockedState = async () => {
    const newBlockedState = !formData.is_blocked;
    const confirmMessage = newBlockedState
      ? 'Block this vehicle? Assignment stops until ops unblocks; drivers cannot activate via the app while blocked.'
      : 'Unblock this vehicle?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.dispatchApiBase}/vehicles/${id}/blocked?blocked=${newBlockedState}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setFormData(prev => ({ ...prev, is_blocked: newBlockedState }));
        alert(`Vehicle ${newBlockedState ? 'blocked' : 'unblocked'} successfully!`);
      } else {
        const data = await response.json();
        alert(`Failed to update blocked state: ${data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error toggling vehicle blocked state:', error);
      alert('Error updating blocked state');
    }
  };

  const handleTogglePartBooking = async () => {
    const newPartState = !formData.is_part;
    const confirmMessage = newPartState
      ? 'Enable Part Booking mode for this vehicle? It will be eligible for multi-booking routes.'
      : 'Disable Part Booking mode for this vehicle?';

    if (!window.confirm(confirmMessage)) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.dispatchApiBase}/vehicles/${id}/part-booking?is_part=${newPartState}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setFormData(prev => ({ ...prev, is_part: newPartState }));
        alert(`Vehicle ${newPartState ? 'enabled for' : 'removed from'} Part Booking mode.`);
      } else {
        const data = await response.json();
        alert(`Failed to update: ${data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error toggling part booking state:', error);
      alert('Error updating vehicle. Please try again.');
    }
  };

  const handleDeleteDriver = async () => {
    if (!driverId) {
      alert('No driver linked to this vehicle.');
      return;
    }
    const confirmed = window.confirm(
      `⚠️ PERMANENT DELETE\n\nThis will completely and permanently delete driver "${formData.driver_name}" (${formData.driver_phone}) and ALL related records including trips, payments, locations, and sessions.\n\nThis action CANNOT be undone.\n\nType OK to confirm.`
    );
    if (!confirmed) return;

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.dispatchApiBase}/drivers/${driverId}/hard`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        alert('✅ Driver permanently deleted successfully.');
        history.push('/app/dispatch/vehicles');
      } else {
        const data = await response.json();
        alert(`Failed to delete driver: ${data.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error deleting driver:', err);
      alert('Error deleting driver. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      
      const updateData = {
        registration_no: formData.registration_no,
        make: formData.make,
        model: formData.model,
        class_: parseInt(formData.class_),
        base_lat: parseFloat(formData.base_lat) || null,
        base_lng: parseFloat(formData.base_lng) || null,
        status: formData.status,
        insurance_number: formData.insurance_number,
        insurance_expiry: formData.insurance_expiry,
        capacity: parseFloat(formData.capacity) || null,
        body_type: formData.body_type,
        specialization: formData.specialization,
        driver: {
          name: formData.driver_name,
          ...(isDriverPhoneEditable ? { phone: normalizedPhone(formData.driver_phone) } : {}),
          language_preference: formData.driver_language_preference,
          active_km_rate: parseFloat(formData.driver_active_km_rate) || null,
          dead_km_rate: parseFloat(formData.driver_dead_km_rate) || null,
          verification_status: formData.driver_verification_status,
        }
      };
      
      const response = await fetch(`${config.dispatchApiBase}/vehicles/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });
      
      if (response.ok) {
        alert('Vehicle updated successfully!');
        history.push('/app/dispatch/vehicles');
      } else {
        const data = await response.json();
        alert(`Failed to update vehicle: ${data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating vehicle:', error);
      alert('Error updating vehicle');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div>
        <Breadcrumb>
          <BreadcrumbItem>YOU ARE HERE</BreadcrumbItem>
          <BreadcrumbItem><a href="#/app/dispatch/vehicles">Vehicle Tracker</a></BreadcrumbItem>
          <BreadcrumbItem active>Edit Vehicle</BreadcrumbItem>
        </Breadcrumb>
        <Widget>
          <div className="text-center">Loading vehicle details...</div>
        </Widget>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Breadcrumb>
          <BreadcrumbItem>YOU ARE HERE</BreadcrumbItem>
          <BreadcrumbItem><a href="#/app/dispatch/vehicles">Vehicle Tracker</a></BreadcrumbItem>
          <BreadcrumbItem active>Edit Vehicle</BreadcrumbItem>
        </Breadcrumb>
        <Widget>
          <Alert color="danger">{error}</Alert>
          <Button color="primary" onClick={() => history.push('/app/dispatch/vehicles')}>
            Back to Vehicle Tracker
          </Button>
        </Widget>
      </div>
    );
  }

  return (
    <div>
      <Breadcrumb>
        <BreadcrumbItem>YOU ARE HERE</BreadcrumbItem>
        <BreadcrumbItem><a href="#/app/dispatch/vehicles">Vehicle Tracker</a></BreadcrumbItem>
        <BreadcrumbItem active>Edit Vehicle #{vehicleId}</BreadcrumbItem>
      </Breadcrumb>
      
      <Row>
        <Col sm={8}>
          <Widget
            title={
              <h5 className="mt-0 mb-0">
                <i className="fa fa-edit mr-xs opacity-70" />{' '}
                Edit Vehicle #{vehicleId}
              </h5>
            }
          >
            <Form onSubmit={handleSubmit}>
              {/* VEHICLE INFORMATION */}
              <Card className="mb-4 border-warning">
                <CardBody>
                  <CardTitle tag="h6" className="text-warning mb-3">
                    <i className="fa fa-truck mr-2" />
                    Vehicle Information
                  </CardTitle>
                  
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="registration_no">
                          <i className="fa fa-lock mr-1" />
                          Registration Number (Read-Only)
                        </Label>
                        <Input
                          type="text"
                          name="registration_no"
                          id="registration_no"
                          value={formData.registration_no}
                          readOnly
                          disabled
                          style={{ 
                            backgroundColor: '#e9ecef', 
                            cursor: 'not-allowed',
                            color: '#495057',
                            fontWeight: '500'
                          }}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="class_">Vehicle Class</Label>
                        <Input
                          type="select"
                          name="class_"
                          id="class_"
                          value={formData.class_}
                          onChange={handleChange}
                        >
                          <option value="">-- Select Class --</option>
                          <option value="1">Class 1 (LCV)</option>
                          <option value="2">Class 2 (MCV)</option>
                          <option value="3">Class 3 (HCV)</option>
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="make">Make</Label>
                        <Input
                          type="text"
                          name="make"
                          id="make"
                          value={formData.make}
                          onChange={handleChange}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="model">Model</Label>
                        <Input
                          type="text"
                          name="model"
                          id="model"
                          value={formData.model}
                          onChange={handleChange}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={4}>
                      <FormGroup>
                        <Label for="status">Status</Label>
                        <Input
                          type="select"
                          name="status"
                          id="status"
                          value={formData.status}
                          onChange={handleChange}
                        >
                          <option value="available">Available</option>
                          <option value="active">Active</option>
                          <option value="pending">Pending</option>
                          <option value="maintenance">Maintenance</option>
                          <option value="inactive">Inactive</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label for="capacity">Capacity (tons)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          name="capacity"
                          id="capacity"
                          value={formData.capacity}
                          onChange={handleChange}
                          placeholder="e.g., 7.5"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label for="body_type">Body Type</Label>
                        <Input
                          type="text"
                          name="body_type"
                          id="body_type"
                          list="vehicle-body-type-presets"
                          value={formData.body_type}
                          onChange={handleChange}
                          placeholder="Select preset or type custom"
                        />
                        <datalist id="vehicle-body-type-presets">
                          <option value="Open" />
                          <option value="Sheeted" />
                          <option value="Closed" />
                        </datalist>
                        <small className="text-muted">
                          Choose Open, Sheeted, or Closed from the list, or enter any custom body type.
                        </small>
                      </FormGroup>
                    </Col>
                  </Row>

                  <FormGroup>
                    <Label for="specialization">Specialization</Label>
                    <Input
                      type="select"
                      name="specialization"
                      id="specialization"
                      value={formData.specialization}
                      onChange={handleChange}
                    >
                      <option value="">None</option>
                      <option value="Tipper">Tipper</option>
                      <option value="Refrigerated">Refrigerated</option>
                    </Input>
                  </FormGroup>

                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="insurance_number">Insurance Number</Label>
                        <Input
                          type="text"
                          name="insurance_number"
                          id="insurance_number"
                          value={formData.insurance_number}
                          onChange={handleChange}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="insurance_expiry">Insurance Expiry</Label>
                        <Input
                          type="date"
                          name="insurance_expiry"
                          id="insurance_expiry"
                          value={formData.insurance_expiry}
                          onChange={handleChange}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <h6 className="mb-3 mt-4">Base Location</h6>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="base_lat">Latitude</Label>
                        <Input
                          type="number"
                          step="0.000001"
                          name="base_lat"
                          id="base_lat"
                          value={formData.base_lat}
                          onChange={handleChange}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="base_lng">Longitude</Label>
                        <Input
                          type="number"
                          step="0.000001"
                          name="base_lng"
                          id="base_lng"
                          value={formData.base_lng}
                          onChange={handleChange}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              {/* REGISTERED OWNER (when not the same as the driver) */}
              {ownerInfo && (
                <Card className="mb-4 border-info">
                  <CardBody>
                    <CardTitle tag="h6" className="text-info mb-3">
                      <i className="fa fa-building mr-2" />
                      Registered owner
                    </CardTitle>
                    <Alert color="light" className="mb-3 border">
                      <small className="text-muted">
                        RC is registered under this person. The driver below may be different.
                      </small>
                    </Alert>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Owner name</Label>
                          <Input
                            type="text"
                            value={ownerInfo.name || '—'}
                            readOnly
                            disabled
                            style={{
                              backgroundColor: '#e9ecef',
                              cursor: 'not-allowed',
                              color: '#495057',
                              fontWeight: '500',
                            }}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>
                            <i className="fa fa-lock mr-1" />
                            Owner phone
                          </Label>
                          <Input
                            type="tel"
                            value={ownerInfo.phone || '—'}
                            readOnly
                            disabled
                            style={{
                              backgroundColor: '#e9ecef',
                              cursor: 'not-allowed',
                              color: '#495057',
                              fontWeight: '500',
                            }}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              )}

              {/* DRIVER INFORMATION */}
              <Card className="mb-4 border-success">
                <CardBody>
                  <CardTitle tag="h6" className="text-success mb-3">
                    <i className="fa fa-user mr-2" />
                    Driver Information (Editable)
                  </CardTitle>
                  
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="driver_name">Driver Name</Label>
                        <Input
                          type="text"
                          name="driver_name"
                          id="driver_name"
                          value={formData.driver_name}
                          onChange={handleChange}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <div className="d-flex align-items-center justify-content-between">
                          <Label for="driver_phone" className="mb-0">
                            {!isDriverPhoneEditable && <i className="fa fa-lock mr-1" />}
                            Phone {isDriverPhoneEditable ? '(Editable)' : '(Read-Only)'}
                          </Label>
                          <div className="d-flex align-items-center" style={{ gap: 10 }}>
                            <Label className="mb-0 text-muted" style={{ fontSize: 12 }}>
                              Editable
                            </Label>
                            <Input
                              id="toggle_driver_phone_editable"
                              name="toggle_driver_phone_editable"
                              type="checkbox"
                              checked={isDriverPhoneEditable}
                              onChange={(e) => setIsDriverPhoneEditable(Boolean(e.target.checked))}
                            />
                          </div>
                        </div>
                        <Input
                          type="tel"
                          name="driver_phone"
                          id="driver_phone"
                          value={formData.driver_phone}
                          onChange={handleChange}
                          disabled={!isDriverPhoneEditable}
                          style={
                            !isDriverPhoneEditable
                              ? {
                                  backgroundColor: '#e9ecef',
                                  cursor: 'not-allowed',
                                  color: '#495057',
                                  fontWeight: '500',
                                }
                              : undefined
                          }
                        />
                        {isDriverPhoneEditable && (
                          <div className="mt-2">
                            <Alert color="warning" className="mb-2" style={{ padding: '8px 10px', fontSize: 12 }}>
                              Editing phone can impact login/communication. If backend update is not supported, use the SQL below.
                            </Alert>
                            {shouldShowPhoneSqlPreview && (
                              <div style={{ border: '1px solid #dee2e6', borderRadius: 6, overflow: 'hidden' }}>
                                <div
                                  className="d-flex align-items-center justify-content-between"
                                  style={{ padding: '8px 10px', background: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}
                                >
                                  <div style={{ fontWeight: 600, fontSize: 12 }}>
                                    SQL preview (updates `users.users` + `users.drivers`)
                                  </div>
                                  <Button type="button" size="sm" color="primary" onClick={copyPhoneSql}>
                                    Copy SQL
                                  </Button>
                                </div>
                                <pre style={{ margin: 0, padding: '10px 12px', fontSize: 12, background: '#fff', whiteSpace: 'pre-wrap' }}>
                                  {phoneUpdateSql}
                                </pre>
                              </div>
                            )}
                          </div>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* READ-ONLY FIELDS */}
                  <Alert color="info" className="mb-3 mt-3">
                    <i className="fa fa-lock mr-2" />
                    <strong>Read-Only Fields</strong><br />
                    <small>License and performance information cannot be edited for security reasons.</small>
                  </Alert>

                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="driver_license_number">
                          <i className="fa fa-lock mr-1" />
                          License Number (Read-Only)
                        </Label>
                        <Input
                          type="text"
                          name="driver_license_number"
                          id="driver_license_number"
                          value={formData.driver_license_number || 'Not available'}
                          readOnly
                          disabled
                          style={{ 
                            backgroundColor: '#e9ecef', 
                            cursor: 'not-allowed',
                            color: '#495057',
                            fontWeight: '500'
                          }}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="driver_license_expiry">
                          <i className="fa fa-lock mr-1" />
                          License Expiry (Read-Only)
                        </Label>
                        <Input
                          type="text"
                          name="driver_license_expiry"
                          id="driver_license_expiry"
                          value={formData.driver_license_expiry || 'Not available'}
                          readOnly
                          disabled
                          style={{ 
                            backgroundColor: '#e9ecef', 
                            cursor: 'not-allowed',
                            color: '#495057',
                            fontWeight: '500'
                          }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={4}>
                      <FormGroup>
                        <Label for="driver_total_trips">
                          <i className="fa fa-lock mr-1" />
                          Total Trips
                        </Label>
                        <Input
                          type="text"
                          name="driver_total_trips"
                          id="driver_total_trips"
                          value={formData.driver_total_trips}
                          readOnly
                          disabled
                          style={{ 
                            backgroundColor: '#e9ecef', 
                            cursor: 'not-allowed',
                            color: '#495057',
                            fontWeight: '500'
                          }}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label for="driver_rating">
                          <i className="fa fa-lock mr-1" />
                          Rating
                        </Label>
                        <Input
                          type="text"
                          name="driver_rating"
                          id="driver_rating"
                          value={formData.driver_rating ? `${formData.driver_rating} ⭐` : 'Not rated'}
                          readOnly
                          disabled
                          style={{ 
                            backgroundColor: '#e9ecef', 
                            cursor: 'not-allowed',
                            color: '#495057',
                            fontWeight: '500'
                          }}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label for="driver_verification_status">Verification Status</Label>
                        <Input
                          type="select"
                          name="driver_verification_status"
                          id="driver_verification_status"
                          value={formData.driver_verification_status}
                          onChange={handleChange}
                        >
                          <option value="pending">Pending</option>
                          <option value="verified">Verified</option>
                          <option value="manual_review_required">Manual Review Required</option>
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>

                  <h6 className="mb-3 mt-3">Editable Driver Fields</h6>
                  <Row>
                    <Col md={4}>
                      <FormGroup>
                        <Label for="driver_language_preference">Language</Label>
                        <Input
                          type="select"
                          name="driver_language_preference"
                          id="driver_language_preference"
                          value={formData.driver_language_preference}
                          onChange={handleChange}
                        >
                          <option value="">Select</option>
                          <option value="english">English</option>
                          <option value="kannada">Kannada</option>
                          <option value="roman_kannada">Roman Kannada</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label for="driver_active_km_rate">Active KM Rate (₹)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          name="driver_active_km_rate"
                          id="driver_active_km_rate"
                          value={formData.driver_active_km_rate}
                          onChange={handleChange}
                          placeholder="0.00"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label for="driver_dead_km_rate">Dead KM Rate (₹)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          name="driver_dead_km_rate"
                          id="driver_dead_km_rate"
                          value={formData.driver_dead_km_rate}
                          onChange={handleChange}
                          placeholder="0.00"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* DANGER ZONE */}
                  {driverId && (
                    <div className="mt-4 p-3" style={{ border: '1px solid #dc3545', borderRadius: '4px', backgroundColor: '#fff5f5' }}>
                      <h6 className="text-danger mb-2">
                        <i className="fa fa-exclamation-triangle mr-2" />
                        Danger Zone
                      </h6>
                      <p className="text-muted mb-2" style={{ fontSize: '0.85em' }}>
                        Permanently delete this driver and <strong>all related records</strong> (trips, payments, locations, sessions). This cannot be undone.
                      </p>
                      <Button
                        type="button"
                        color="danger"
                        onClick={handleDeleteDriver}
                        disabled={isSubmitting}
                        size="sm"
                      >
                        <i className="fa fa-trash mr-1" />
                        Delete Driver Permanently
                      </Button>
                    </div>
                  )}
                </CardBody>
              </Card>

              <FormGroup className="mt-4">
                <Button 
                  type="submit" 
                  color="success" 
                  disabled={isSubmitting}
                  className="mr-2"
                >
                  {isSubmitting ? 'Updating...' : 'Update Vehicle & Driver'}
                </Button>
                <Button 
                  type="button"
                  color={formData.is_active ? 'success' : 'danger'}
                  onClick={handleToggleActiveState}
                  disabled={isSubmitting}
                  className="mr-2"
                  title={formData.is_active ? 'Click to deactivate vehicle' : 'Click to activate vehicle'}
                >
                  <i className={`fa fa-${formData.is_active ? 'check-circle' : 'times-circle'} mr-1`} />
                  {formData.is_active ? 'Active' : 'Inactive'}
                </Button>
                <Button
                  type="button"
                  color="warning"
                  onClick={handleToggleBlockedState}
                  disabled={isSubmitting}
                  className="mr-2"
                  title={formData.is_blocked ? 'Ops blocked — click to unblock' : 'Ops-only: block vehicle'}
                  style={
                    formData.is_blocked
                      ? { backgroundColor: '#f59e0b', borderColor: '#d97706', color: '#fff' }
                      : { backgroundColor: '#fef3c7', borderColor: '#fbbf24', color: '#92400e' }
                  }
                >
                  <i className={`fa fa-${formData.is_blocked ? 'ban' : 'unlock-alt'} mr-1`} />
                  {formData.is_blocked ? 'Blocked (ops)' : 'Not blocked'}
                </Button>
                <Button
                  type="button"
                  color={formData.is_part ? 'primary' : 'secondary'}
                  onClick={handleTogglePartBooking}
                  disabled={isSubmitting}
                  className="mr-2"
                  title={formData.is_part ? 'Click to disable Part Booking mode' : 'Click to enable Part Booking mode'}
                  style={formData.is_part ? { backgroundColor: '#6f42c1', borderColor: '#6f42c1' } : {}}
                >
                  <i className={`fa fa-${formData.is_part ? 'check-circle' : 'plus-circle'} mr-1`} />
                  {formData.is_part ? 'Part Booking ✓' : 'Make Part Booking Vehicle'}
                </Button>
                <Button
                  type="button"
                  color="info"
                  onClick={openDocsModal}
                  className="mr-2"
                >
                  <i className="fa fa-folder-open mr-1" />
                  Vehicle Docs
                </Button>
                <Button 
                  type="button"
                  color="default" 
                  onClick={() => history.push('/app/dispatch/vehicles')}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              </FormGroup>
            </Form>
          </Widget>
        </Col>
        <Col sm={4}>
          <Widget title="📝 Edit Notes">
            <Alert color="warning" className="fs-sm">
              <i className="fa fa-exclamation-triangle mr-2" />
              <strong>Important:</strong>
            </Alert>
            <ul className="fs-sm text-muted">
              <li>🔒 Registration number is read-only</li>
              <li>🔒 Driver phone is read-only</li>
              <li>🔒 License information is read-only</li>
              <li>✏️ Verification status can be edited</li>
              <li>Changes affect both vehicle & driver</li>
              <li>Status changes are immediate</li>
            </ul>
          </Widget>
        </Col>
      </Row>
      {/* ── Vehicle Docs Modal ──────────────────────────────────────── */}
      <Modal
        isOpen={showDocsModal}
        toggle={() => setShowDocsModal(false)}
        size="lg"
        scrollable
      >
        <ModalHeader toggle={() => setShowDocsModal(false)}>
          <i className="fa fa-folder-open mr-2 text-info" />
          Vehicle Documents — #{vehicleId}
        </ModalHeader>
        <ModalBody>
          {docsLoading && (
            <div style={{ textAlign: 'center', padding: 32 }}>
              <Spinner color="info" />
              <p className="mt-2 text-muted">Loading documents...</p>
            </div>
          )}
          {docsError && (
            <Alert color="danger">
              <i className="fa fa-exclamation-circle mr-1" />{docsError}
            </Alert>
          )}
          {!docsLoading && !docsError && driverDocs && (
            <>
              {/* Summary bar */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                {DOC_TYPES.map(dt => (
                  <Badge
                    key={dt.key}
                    color={driverDocs[dt.key]?.present ? 'success' : 'danger'}
                    style={{ fontSize: 12, padding: '4px 10px' }}
                  >
                    <i className={`fa ${dt.icon} mr-1`} />
                    {dt.label}
                    {driverDocs[dt.key]?.present ? ' ✓' : ' ✗'}
                  </Badge>
                ))}
              </div>
              {/* Individual cards */}
              {DOC_TYPES.map(dt => (
                <DocCard
                  key={dt.key}
                  docType={dt}
                  docData={driverDocs[dt.key]}
                  vehicleId={vehicleId}
                  onRefresh={() => fetchDriverDocs(vehicleId)}
                />
              ))}
            </>
          )}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default VehicleEdit;
